import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, User, ChevronRight, Share2 } from 'lucide-react';
import { Button } from './ui/Button';
export interface BlogPostData {
  title: string;
  slug: string;
  excerpt: string;
  content: React.ReactNode;
  author: string;
  date: string;
  readingTime: string;
  image: string;
  keywords: string;
  metaDescription: string;
  category?: string;
  wordCount?: number;
}
interface BlogPostProps {
  post: BlogPostData;
  onJoinClick?: () => void;
}
export function BlogPost({
  post,
  onJoinClick
}: BlogPostProps) {
  const siteUrl = 'https://holdmyvan.com.au';
  const postUrl = `${siteUrl}/blog/${post.slug}`;
  // Enhanced Article Schema with more details
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    alternativeHeadline: post.excerpt,
    image: {
      '@type': 'ImageObject',
      url: post.image,
      width: 1200,
      height: 630
    },
    author: {
      '@type': 'Organization',
      name: post.author,
      url: siteUrl
    },
    publisher: {
      '@type': 'Organization',
      name: 'Hold My Van',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://cdn.magicpatterns.com/uploads/8iyBWSo8vRExHMrz3iz275/Hold_My_Van_Full_Colour_Logo_1500px_V1.png',
        width: 200,
        height: 200
      }
    },
    datePublished: '2025-01-24T10:00:00+08:00',
    dateModified: '2025-01-24T10:00:00+08:00',
    description: post.metaDescription,
    articleBody: post.excerpt,
    articleSection: post.category || 'Storage Insights',
    wordCount: post.wordCount || 1800,
    keywords: post.keywords,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl
    },
    inLanguage: 'en-AU'
  };
  // BreadcrumbList Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: `${siteUrl}/blog`
    }, {
      '@type': 'ListItem',
      position: 3,
      name: post.title,
      item: postUrl
    }]
  };
  return <article className="bg-gray-50 font-sans h-full" data-id="element-16">
      <Helmet data-id="element-17">
        {/* Primary Meta Tags */}
        <html lang="en-AU" data-id="element-18" />
        <title data-id="element-19">{post.title} | Hold My Van Blog</title>
        <meta name="title" content={`${post.title} | Hold My Van Blog`} data-id="element-20" />
        <meta name="description" content={post.metaDescription} data-id="element-21" />
        <meta name="keywords" content={post.keywords} data-id="element-22" />
        <meta name="author" content={post.author} data-id="element-23" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" data-id="element-24" />
        <link rel="canonical" href={postUrl} data-id="element-25" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" data-id="element-26" />
        <meta property="og:url" content={postUrl} data-id="element-27" />
        <meta property="og:title" content={post.title} data-id="element-28" />
        <meta property="og:description" content={post.metaDescription} data-id="element-29" />
        <meta property="og:image" content={post.image} data-id="element-30" />
        <meta property="og:image:width" content="1200" data-id="element-31" />
        <meta property="og:image:height" content="630" data-id="element-32" />
        <meta property="og:image:alt" content={post.title} data-id="element-33" />
        <meta property="og:site_name" content="Hold My Van" data-id="element-34" />
        <meta property="og:locale" content="en_AU" data-id="element-35" />
        <meta property="article:published_time" content="2025-01-24T10:00:00+08:00" data-id="element-36" />
        <meta property="article:modified_time" content="2025-01-24T10:00:00+08:00" data-id="element-37" />
        <meta property="article:author" content={post.author} data-id="element-38" />
        <meta property="article:section" content={post.category || 'Storage Insights'} data-id="element-39" />
        <meta property="article:tag" content={post.keywords} data-id="element-40" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-41" />
        <meta name="twitter:url" content={postUrl} data-id="element-42" />
        <meta name="twitter:title" content={post.title} data-id="element-43" />
        <meta name="twitter:description" content={post.metaDescription} data-id="element-44" />
        <meta name="twitter:image" content={post.image} data-id="element-45" />
        <meta name="twitter:image:alt" content={post.title} data-id="element-46" />

        {/* Additional SEO */}
        <meta name="geo.region" content="AU-WA" data-id="element-47" />
        <meta name="geo.placename" content="Perth" data-id="element-48" />

        {/* Schema Markup */}
        <script type="application/ld+json" data-id="element-49">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json" data-id="element-50">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Breadcrumbs with Schema markup */}
      <div className="bg-[#fbf7dd] border-b border-gray-100 mb-8" data-id="element-51">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-52">
          <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb" data-id="element-53">
            <a href="/" className="hover:text-[#175445] transition-colors" data-id="element-54">
              Home
            </a>
            <ChevronRight className="h-4 w-4 mx-2" aria-hidden="true" data-id="element-55" />
            <a href="/blog" className="hover:text-[#175445] transition-colors" data-id="element-56">
              Blog
            </a>
            <ChevronRight className="h-4 w-4 mx-2" aria-hidden="true" data-id="element-57" />
            <span className="text-gray-900 truncate max-w-[200px] sm:max-w-none font-medium" aria-current="page" data-id="element-58">
              {post.title}
            </span>
          </nav>
        </div>
      </div>

      {/* Main Content Area - removed max-w-4xl constraint to allow grid layout control */}
      <div className="w-full" data-id="element-59">
        {/* Header */}
        <header className="mb-10 text-center max-w-4xl mx-auto" data-id="element-60">
          <div className="inline-block bg-[#175445]/10 text-[#175445] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6" data-id="element-61">
            {post.category || 'Storage Insights'}
          </div>
          <h1 className="text-3xl md:text-5xl font-display font-bold text-gray-900 mb-6 leading-tight" data-id="element-62">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center justify-center gap-6 text-gray-500 text-sm" data-id="element-63">
            <div className="flex items-center" data-id="element-64">
              <User className="h-4 w-4 mr-2 text-[#175445]" aria-hidden="true" data-id="element-65" />
              <span itemProp="author" data-id="element-66">{post.author}</span>
            </div>
            <div className="flex items-center" data-id="element-67">
              <Calendar className="h-4 w-4 mr-2 text-[#175445]" aria-hidden="true" data-id="element-68" />
              <time dateTime="2025-01-24" itemProp="datePublished" data-id="element-69">
                {post.date}
              </time>
            </div>
            <div className="flex items-center" data-id="element-70">
              <Clock className="h-4 w-4 mr-2 text-[#175445]" aria-hidden="true" data-id="element-71" />
              {post.readingTime}
            </div>
          </div>
        </header>

        {/* Optimized Featured Image */}
        <figure className="mb-12 rounded-2xl overflow-hidden shadow-xl" data-id="element-72">
          <img src={post.image} alt={`${post.title} - Caravan and boat storage facility in Perth's northern corridor`} className="w-full h-auto object-cover max-h-[500px]" loading="lazy" width="1200" height="500" itemProp="image" data-id="element-73" />
        </figure>

        {/* Content with semantic HTML */}
        <div className="prose prose-lg max-w-none bg-[#fbf7dd] p-8 md:p-12 rounded-2xl shadow-sm border border-gray-100" itemProp="articleBody" data-id="element-74">
          <style data-id="element-75">{`
            .prose h2 {
              font-size: 2.5rem;
              line-height: 1.2;
              font-weight: 700;
              color: #1a202c;
              margin-top: 2.5rem;
              margin-bottom: 1.5rem;
            }
            .prose h3 {
              font-size: 2rem;
              line-height: 1.3;
              font-weight: 700;
              color: #2d3748;
              margin-top: 2rem;
              margin-bottom: 1rem;
            }
            .prose p {
              color: #4a5568;
              line-height: 1.8;
              margin-bottom: 1.5rem;
            }
            .prose ul {
              margin-top: 1rem;
              margin-bottom: 1.5rem;
            }
            .prose li {
              color: #4a5568;
              line-height: 1.8;
            }
            .prose a {
              color: #175445;
              text-decoration: underline;
            }
            .prose a:hover {
              color: #123B35;
            }
            .prose strong {
              color: #1a202c;
              font-weight: 600;
            }
          `}</style>
          {post.content}
        </div>

        {/* Internal Links Section for SEO */}
        <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200" data-id="element-76">
          <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-77">
            Related Resources
          </h3>
          <ul className="space-y-2" data-id="element-78">
            <li data-id="element-79">
              <a href="/#features" className="text-[#175445] hover:text-[#123B35] font-medium" data-id="element-80">
                → View Our Storage Facility Features
              </a>
            </li>
            <li data-id="element-81">
              <a href="/#pricing" className="text-[#175445] hover:text-[#123B35] font-medium" data-id="element-82">
                → Check Storage Pricing & Availability
              </a>
            </li>
            <li data-id="element-83">
              <a href="/#contact" className="text-[#175445] hover:text-[#123B35] font-medium" data-id="element-84">
                → Contact Us for More Information
              </a>
            </li>
          </ul>
        </div>

        {/* Share & Tags */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-85">
          <div className="flex gap-2 flex-wrap" data-id="element-86">
            {post.keywords.split(', ').slice(0, 5).map(tag => <span key={tag} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium" itemProp="keywords" data-id="element-87">
                  #{tag.replace(/\s+/g, '')}
                </span>)}
          </div>
          <button onClick={() => {
          if (navigator.share) {
            navigator.share({
              title: post.title,
              text: post.excerpt,
              url: window.location.href
            });
          }
        }} className="flex items-center gap-2 text-gray-500 hover:text-[#175445] transition-colors" aria-label="Share this article" data-id="element-88">
            <Share2 className="h-4 w-4" aria-hidden="true" data-id="element-89" />
            <span className="text-sm font-medium" data-id="element-90">Share Article</span>
          </button>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-[#175445] rounded-2xl p-8 md:p-12 text-center text-[#fbf7dd] relative overflow-hidden" data-id="element-91">
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-[#fbf7dd] opacity-10 rounded-full blur-3xl" aria-hidden="true" data-id="element-92"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 bg-[#FFC107] opacity-20 rounded-full blur-3xl" aria-hidden="true" data-id="element-93"></div>

          <div className="relative z-10" data-id="element-94">
            <h3 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-95">
              Ready to secure your storage?
            </h3>
            <p className="text-lg text-gray-100 mb-8 max-w-2xl mx-auto" data-id="element-96">
              Don't let storage headaches stop your next adventure. Join the
              priority list for secure, accessible storage in Perth's northern
              corridor.
            </p>
            <Button variant="secondary" size="lg" className="bg-[#FFC107] text-black hover:bg-[#FFD54F] border-none shadow-lg" onClick={onJoinClick} data-id="element-97">
              Join Priority List
            </Button>
          </div>
        </div>
      </div>
    </article>;
}